import addFormats from "ajv-formats"
import ajvErrors from "ajv-errors"
import Ajv from "ajv"
import i18next from "i18next"

import { isNotEmpty, isEmailValid } from "./validation"
import { BIKE_ID_MIN_LENGTH } from "./constants"
import { AddBikeErrorProps } from "../features/BikeCollectionImport"
import { BikeImport } from "../models"

const DEFAULT_BIKE_TYPE = i18next.t("select_type", { ns: "bikeCollectionImport" })
const DEFAULT_BIKE_BRAND = i18next.t("select_brand", { ns: "bikeCollectionImport" })

const PHONE_REGEX = /^\+[1-9]\d{1,14}$/
const CONSUMER_ID_REGEX = /^[0-9]+$/

export const VALIDATED_IMPORT_STATUS = 0
export const REFUSED_IMPORT_STATUS = 1


// ********************************************************************************************
//                     BIKE DESCRIPTION VALIDATION
// ********************************************************************************************

type addBikeErrorProps = ({ bike, errorTextArr }: AddBikeErrorProps) => void

export const isBikeCodeValid = (str: string) => {
  return str?.trim().length >= BIKE_ID_MIN_LENGTH
}

/**
 * Checks if the bike is correctly formatted with the schema passed as a parameter.
 * Add an error if not and return a boolean
 */
export const isBikeValid = (schema, bike: BikeImport, addBikeError: addBikeErrorProps) => {
  const ajv: Ajv = new Ajv({ allErrors: true }) // allErrors = allows us to custom errors
  addFormats(ajv) // allows us to use avj default formats for validation, as email for example.
  ajvErrors(ajv) // allows us to use custom errors (in schema.js)
  const validate = ajv.compile(schema)

  // remove technical id to prevent AJV additionalProperties error.
  const bikeWithoutID = Object.assign({}, bike)
  delete bikeWithoutID.id

  const isValid = validate(bikeWithoutID)
  const errorTextArr = validate?.errors && validate?.errors[0].message.toString()

  if (!isValid) addBikeError({ bike, errorTextArr })

  return isValid
}

/**
 * Check if engine is ensuring consistency with the VAE data
 */
export const isEngineNumberValid = (bike: BikeImport, addBikeError: addBikeErrorProps) => {
  const isElectric = bike.bike_description.is_electric
  const isEngineNotEmpty = isNotEmpty(bike.bike_description?.engine_serial_number)
  if (isEngineNotEmpty && !isElectric) {
    addBikeError({ bike, errorTextArr: i18next.t("error_bike_description_engine_incoherence", { ns: "bikeCollectionImport" }) })
    return false
  }
  return true
}

/**
 * Check if battery is ensuring consistency with the VAE data
 */
export const isBatteryNumberValid = (bike: BikeImport, addBikeError: addBikeErrorProps) => {
  const isElectric = bike.bike_description.is_electric
  const isBatteryNotEmpty = isNotEmpty(bike.bike_description?.battery_serial_number)
  if (isBatteryNotEmpty && !isElectric) {
    addBikeError({ bike, errorTextArr: i18next.t("error_bike_description_battery_incoherence", { ns: "bikeCollectionImport" }) })
    return false
  }
  return true
}

/**
 * Check if the type (VTT, Ville etc.) is given
 */
export const isTypeValid = (bike: BikeImport, addBikeError: addBikeErrorProps) => {
  const bikeType = bike.bike_description.gear_type
  if (bikeType === DEFAULT_BIKE_TYPE) {
    addBikeError({ bike, errorTextArr: i18next.t("error_bike_description_default_type", { ns: "bikeCollectionImport" }) })
    return false
  }
  if (!isNotEmpty(bikeType)) {
    addBikeError({ bike, errorTextArr: i18next.t("error_bike_description_empty_type", { ns: "bikeCollectionImport" }) })
    return false
  }
  return true
}

/**
 * Check if the brand (Peugeot, Rockrider) is given
 */
export const isBrandValid = (bike: BikeImport, addBikeError: addBikeErrorProps) => {
  const bikeBrand = bike.bike_description.brand
  if (bikeBrand === DEFAULT_BIKE_BRAND) {
    addBikeError({ bike, errorTextArr: i18next.t("error_bike_description_default_brand", { ns: "bikeCollectionImport" }) })
    return false
  }
  if (!isNotEmpty(bikeBrand)) {
    addBikeError({ bike, errorTextArr: i18next.t("error_bike_description_empty_brand", { ns: "bikeCollectionImport" }) })
    return false
  }
  return true
}

/**
 * Check if model is given
 */
export const isModelValid = (bike: BikeImport, addBikeError: addBikeErrorProps) => {
  const bikeModel = bike.bike_description.model
  if (!isNotEmpty(bikeModel)) {
    addBikeError({ bike, errorTextArr: i18next.t("error_bike_description_empty_model", { ns: "bikeCollectionImport" }) })
    return false
  }
  return true
}

/**
 * Check if color is given
 */
export const isColorValid = (bike: BikeImport, addBikeError: addBikeErrorProps) => {
  const bikeColor = bike.bike_description.color
  if (!isNotEmpty(bikeColor)) {
    addBikeError({ bike, errorTextArr: i18next.t("error_bike_description_empty_color", { ns: "bikeCollectionImport" }) })
    return false
  }
  return true
}

// ********************************************************************************************
//                     OWNER VALIDATION
// ********************************************************************************************

/**
 * Check if at least one information is given
 */
export const isPrivateOwner = (bike) => {
  return isNotEmpty(bike.bike_owner.private_owner?.last_name)
    || isNotEmpty(bike.bike_owner.private_owner?.first_name)
    || isNotEmpty(bike.bike_owner.private_owner?.phone)
    || isNotEmpty(bike.bike_owner.private_owner?.mail)
}

/**
 * Check if at least one company information is given
 */
export const isCompanyOwner = (bike) => {
  return isNotEmpty(bike.bike_owner.company_owner?.social_reason)
    || isNotEmpty(bike.bike_owner.company_owner?.phone)
    || isNotEmpty(bike.bike_owner.company_owner?.mail)
}

/**
 * Check if consumer id is given
 */
export const isOwnerByConsumerId = (bike) => {
  return isNotEmpty(bike.bike_owner.owner_by_id?.consumer_id)
}

/**
 * Check if bikes is associate to multiple owners
 */
export const isNoMultipleOwners = (bike, addBikeError) => {
  const ownerTypes = {
    privateOwner: isPrivateOwner(bike),
    companyOwner: isCompanyOwner(bike),
    consumerOwner: isOwnerByConsumerId(bike)
  }
  const isOnlyOneOwnerType = Object.values(ownerTypes).filter(isType => isType).length === 1

  if (!isOnlyOneOwnerType) addBikeError(bike, i18next.t("error_bike_description_multiple_owners", { ns: "bikeCollectionImport" }))

  return isOnlyOneOwnerType
}

/**
 * Check if at least one owner is given
 */
export const isOwnerExist = (bike, addBikeError) => {
  if (isPrivateOwner(bike) || isCompanyOwner(bike) || isOwnerByConsumerId(bike)) return true
  addBikeError(bike, i18next.t("error_bike_description_no_owner", { ns: "bikeCollectionImport" }))
  return false
}

/**
 * Check if owner is valid
 */
export const isOwnerValid = (bike, addBikeError) => {
  const gender = bike.bike_owner?.gender
  switch (gender) {
    case 1:
      return isPrivateOwnerValid(bike, addBikeError)
    case 2:
      return isCompanyOwnerValid(bike, addBikeError)
    default:
      return isConsumerIdValid(bike, addBikeError)
  }
}

/**
 * Check if the private owner is valid
 */
const isPrivateOwnerValid = (bike, addBikeError) => {
  const owner = bike.bike_owner
  if (!isNotEmpty(owner.first_name)) {
    addBikeError(bike, i18next.t("error_bike_owner_private_empty_first_name", { ns: "bikeCollectionImport" }))
    return false
  }
  if (!isNotEmpty(owner.last_name)) {
    addBikeError(bike, i18next.t("error_bike_owner_private_empty_last_name", { ns: "bikeCollectionImport" }))
    return false
  }
  if (!isNotEmpty(owner.phone)) {
    addBikeError(bike, i18next.t("error_bike_owner_empty_phone", { ns: "bikeCollectionImport" }))
    return false
  }
  if (!isPhoneValid(owner.phone)) {
    addBikeError(bike, i18next.t("error_bike_owner_format_phone", { ns: "bikeCollectionImport" }))
    return false
  }
  if (!isNotEmpty(owner.mail)) {
    addBikeError(bike, i18next.t("error_bike_owner_empty_mail", { ns: "bikeCollectionImport" }))
    return false
  }
  if (!isEmailValid(owner.mail)) {
    addBikeError(bike, i18next.t("error_bike_owner_format_mail", { ns: "bikeCollectionImport" }))
    return false
  }
  return true
}

/**
 * Check if the company owner is valid
 */
const isCompanyOwnerValid = (bike, addBikeError) => {
  const owner = bike.bike_owner
  if (!isNotEmpty(owner.social_reason)) {
    addBikeError(bike, i18next.t("error_bike_owner_format_mail", { ns: "bikeCollectionImport" }))
    return false
  }
  if (!isNotEmpty(owner.phone)) {
    addBikeError(bike, i18next.t("error_bike_owner_empty_phone", { ns: "bikeCollectionImport" }))
    return false
  }
  if (!isPhoneValid(owner.phone)) {
    addBikeError(bike, i18next.t("error_bike_owner_format_phone", { ns: "bikeCollectionImport" }))
    return false
  }
  if (!isNotEmpty(owner.mail)) {
    addBikeError(bike, i18next.t("error_bike_owner_empty_mail", { ns: "bikeCollectionImport" }))
    return false
  }
  if (!isEmailValid(owner.mail)) {
    addBikeError(bike, i18next.t("error_bike_owner_format_mail", { ns: "bikeCollectionImport" }))
    return false
  }
  return true
}

/**
 * Check if the consumer id is valid
 */
const isConsumerIdValid = (bike, addBikeError) => {
  const consumerId = bike.bike_owner.consumer_id.toString()
  if (!consumerId.match(CONSUMER_ID_REGEX)) {
    addBikeError(bike, i18next.t("error_bike_owner_consumer_id_format", { ns: "bikeCollectionImport" }))
    return false
  }
  return true
}

/**
 * Check if the phone number is valid
 */
export const isPhoneValid = (phone) => {
  // AWS regex to check international phone numbers
  if (!phone.match(PHONE_REGEX)) return false
  return true
}
